import React from 'react';
import ResponsiveEmbed from 'react-bootstrap/ResponsiveEmbed';
import { xhrClient } from '../../services/xhrClient';

interface MediaPlayerProps {
    id: string;
    name: string;
    videoUrl: string;
    onVideoPlaybackError?: () => void;
    onVideoNotAvailable?: () => void;
}

declare global {
    interface Window {
        onYouTubeIframeAPIReady: () => void;
    }
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({
    id,
    name,
    videoUrl,
    onVideoPlaybackError = () => {
        /* Default empty handler */
    },
    onVideoNotAvailable = () => {
        /* Default empty handler */
    },
}) => {
    const [isVideoHostedOnYoutube] = React.useState(() => videoUrl.startsWith('https://www.youtube.com/'));
    const [isVideoHostedOnVimeo] = React.useState(() => videoUrl.startsWith('https://player.vimeo.com/'));
    const [augmentedVideoUrl] = React.useState(() => {
        if (isVideoHostedOnYoutube) {
            const separator = videoUrl.indexOf('?') > 0 ? '&' : '?';
            return `${videoUrl}${separator}enablejsapi=1`;
        }
        return videoUrl;
    });

    React.useEffect(() => {
        if (isVideoHostedOnYoutube && !window.YT && !window.onYouTubeIframeAPIReady) {
            window.onYouTubeIframeAPIReady = (): void => {
                try {
                    const player = new window.YT.Player(id, {
                        events: {
                            onReady: () => {
                                if (player.getPlayerState() === -1) {
                                    onVideoNotAvailable();
                                }
                            },
                            onError: () => onVideoPlaybackError(),
                        },
                    });
                } catch (e) {
                    onVideoPlaybackError();
                }
            };
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            tag.onerror = () => onVideoPlaybackError();
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);

            return function cleanup() {
                window.onYouTubeIframeAPIReady = () => {};
                onVideoNotAvailable = () => {};
                onVideoPlaybackError = () => {};
            };
        } else if (isVideoHostedOnVimeo) {
            const { cancel, isCancel, promise } = xhrClient.checkIfVimeoVideoExists(videoUrl);

            (async function getResponse() {
                try {
                    await promise;
                } catch (thrown) {
                    if (!isCancel(thrown)) {
                        console.error(thrown);
                        onVideoNotAvailable();
                    }
                }
            })();

            return function cleanup() {
                cancel(`Canceled video load for "${videoUrl}"`);
            };
        }
    }, []);

    return (
        <ResponsiveEmbed aspectRatio="16by9">
            <iframe id={id} data-testid={id} src={augmentedVideoUrl} title={name} />
        </ResponsiveEmbed>
    );
};

export { MediaPlayer, MediaPlayerProps };
